import {
  Home,
  BarChart3,
  MessageSquare,
  Phone,
  Upload,
  SettingsIcon,
  ShoppingCart,
  Truck,
  History,
  Search,
  Brain,
  PanelLeftClose,
  PanelLeftOpen,
  ShieldCheck,
  UserIcon,
  ClipboardList,
  LockIcon,
} from "lucide-react";
import { END_POINT } from "constants/apiConstant";
const { MOUSER, ARROW, DIGIKEY, ELEMENT14, AVNET, TTI, TI } = END_POINT;

// Admin modules and permissions
export const COMPANY_MENU_MODULES = {
  DASHBOARD: "dashboard",
  UPLOADED_BOM: "uploaded_bom",
  SEARCH: "search",
  BOM_ANALYSIS: "bom_analysis",
  ORDERS: "orders",
  SHIPMENTS: "shipments",
  HISTORY: "history",
  SUPPLIERS: "suppliers",
  ANALYTICS: "analytics",
  SETTINGS: "settings",
  BOM_AI: "bom_ai",
};

export const PERMISSIONS = {
  VIEW: "view",
  ADD: "add",
  EDIT: "edit",
  DELETE: "delete",
};

export const ITEMS_PER_PAGE = 10;
export const PAGE_VIEW_OPTIONS = [10, 20, 50, 100];

export const ROLE_OPTIONS = [
  { value: "super_admin", label: "Super Admin", icon: LockIcon },
  { value: "user", label: "User", icon: UserIcon },
  { value: "bom_admin", label: "Bom Admin", icon: ShieldCheck },
  { value: "order_admin", label: "Order Admin", icon: ClipboardList },
];

export const ALL_MENU_ITEMS = [
  {
    name: "Dashboard",
    href: "/dashboard",
    icon: Home,
    roles: ["bom_admin", "user", "order_admin", "super_admin"],
  },
  {
    name: "Bom Management",
    href: "/uploaded-bom",
    icon: Upload,
    roles: ["bom_admin", "super_admin"],
  },
  {
    name: "Search",
    href: "/search",
    icon: Search,
    roles: ["bom_admin", "user", "order_admin", "super_admin"],
  },
  {
    name: "BOM Analysis",
    href: "/bom-analysis",
    icon: MessageSquare,
    roles: ["bom_admin", "super_admin"],
  },
  {
    name: "Orders",
    href: "/orders",
    icon: ShoppingCart,
    roles: ["bom_admin", "order_admin", "super_admin"],
  },
  {
    name: "Shipments",
    href: "/shipments",
    icon: Truck,
    roles: ["bom_admin", "user", "order_admin", "super_admin"],
  },
  {
    name: "History",
    href: "/history",
    icon: History,
    roles: ["bom_admin", "user", "order_admin", "super_admin"],
    hideInSidebar: true,
  },
  {
    name: "Suppliers",
    href: "/suppliers",
    icon: Phone,
    roles: ["bom_admin", "user", "order_admin", "super_admin"],
    hideInSidebar: true,
  },
  {
    name: "Analytics",
    href: "/analytics",
    icon: BarChart3,
    roles: ["bom_admin", "user", "order_admin", "super_admin"],
  },
  {
    name: "Settings",
    href: "/settings",
    icon: SettingsIcon,
    roles: ["bom_admin", "order_admin", "super_admin"],
  },
  {
    name: "BOM AI",
    href: "/bom-ai",
    icon: Brain,
    roles: ["bom_admin", "order_admin", "super_admin"],
  },
];

export const pageData = {
  "/dashboard": {
    title: "Dashboard",
    subtitle: "Overview of your BOM procurement activities",
  },
  "/cart": {
    title: "Cart",
    subtitle: "Your shopping cart details",
  },
  "/uploaded-bom": {
    title: "Uploaded Bom",
    subtitle: "View your uploaded BOM files",
  },
  "/search": {
    title: "Search",
    subtitle: "Search for parts and components",
  },
  "/bom-analysis": {
    title: "BOM Analysis",
    subtitle: "MCU_Board_v3.2 - 156 components analyzed",
  },
  "/orders": {
    title: "Orders",
    subtitle: "Manage and track your procurement orders",
  },
  "/shipments": {
    title: "Shipments",
    subtitle: "Track your shipments and deliveries",
  },
  "/history": {
    title: "History",
    subtitle: "Complete history of your procurement activities",
  },
  "/suppliers": {
    title: "Suppliers",
    subtitle: "Manage your supplier connections and API integrations",
  },
  "/analytics": {
    title: "Analytics",
    subtitle: "Insights and performance metrics for your procurement activities",
  },
  "/settings": {
    title: "Settings",
    subtitle: "Configure your BOM Intelligence preferences and integrations",
  },
  "/bom-column-mapping/:id": {
    title: "BOM Column Mapping",
    subtitle: "Map your BOM columns to the correct fields",
  },
  "/my-orders/detail/:id": {
    title: "Order Details",
    subtitle: "View the details of your procurement order",
  },
  "/my-orders": {
    title: "My Orders",
    subtitle: "Manage and track your procurement orders",
  },
  "/sales-order-history": {
    title: "Order History",
    subtitle: "Track your current order",
  },
  "/bom-ai": {
    title: "BOM AI",
    subtitle: "BOM AI Lists",
  },
};

export const DISTRIBUTORS = [
  { name: "Mouser", key: "mouser", api: MOUSER },
  { name: "Arrow", key: "arrow", api: ARROW },
  { name: "Digikey", key: "digikey", api: DIGIKEY },
  { name: "Element14", key: "element14", api: ELEMENT14 },
  { name: "Avnet", key: "avnet", api: AVNET },
  { name: "TTI", key: "tti", api: TTI },
  { name: "TI", key: "ti", api: TI },
  { name: "TME", key: "tme" },
  // Rutronik, like TME, is served by the lambda search pipeline - no per-distributor PHP route.
  { name: "Rutronik", key: "rutronik" },
];

export const PRIMARY_APPLICATIONS = [
  {
    label: "Aerospace",
    value: "Aerospace",
  },
  {
    label: "Automotive",
    value: "Automotive",
  },
  {
    label: "Computer & Peripherals",
    value: "Computer & Peripherals",
  },
  {
    label: "Consumer Electronics",
    value: "Consumer Electronics",
  },
  {
    label: "Industrial",
    value: "Industrial",
  },
  {
    label: "Medical",
    value: "Medical",
  },
  {
    label: "Military",
    value: "Military",
  },
];

export const SECONDARY_APPLICATIONS = {
  Aerospace: [
    {
      label: "Aircraft, commercial transport",
      value: "Aircraft, commercial transport",
    },
    {
      label: "Aircraft, UAV",
      value: "Aircraft, UAV",
    },
    {
      label: "Communications equipment",
      value: "Communications equipment",
    },
    {
      label: "Computer systems",
      value: "Computer systems",
    },
    {
      label: "Entertainment systems",
      value: "Entertainment systems",
    },
    {
      label: "Sensors, controls, test equipment",
      value: "Sensors, controls, test equipment",
    },
    {
      label: "Safety systems",
      value: "Safety systems",
    },
    {
      label: "Satellites, commercial",
      value: "Satellites, commercial",
    },
    {
      label: "Other",
      value: "Other",
    },
  ],
  Automotive: [
    {
      label: "Drive train",
      value: "Drive train",
    },
    {
      label: "Instrumentation",
      value: "Instrumentation",
    },
    {
      label: "Safety systems",
      value: "Safety systems",
    },

    {
      label: "Other",
      value: "Other",
    },
  ],
  "Computer & Peripherals": [
    {
      label: "Accessories",
      value: "Accessories",
    },
    {
      label: "Desktop",
      value: "Desktop",
    },
    {
      label: "Laptop",
      value: "Laptop",
    },
    "Desktop",
    {
      label: "Server/storage",
      value: "Server/storage",
    },
    {
      label: "Other",
      value: "Other",
    },
  ],
  "Consumer Electronics": [
    {
      label: "Audio/video",
      value: "Audio/video",
    },
    {
      label: "Gaming systems",
      value: "Gaming systems",
    },
    {
      label: "Home appliances",
      value: "Home appliances",
    },
    {
      label: "Lighting systems",
      value: "Lighting systems",
    },
    {
      label: "Wired communications",
      value: "Wired communications",
    },
    {
      label: "Wireless communications",
      value: "Wireless communications",
    },
    {
      label: "Other",
      value: "Other",
    },
  ],
  Industrial: [
    {
      label: "Appliances/white goods",
      value: "Appliances/white goods",
    },
    {
      label: "Automation and control",
      value: "Automation and control",
    },
    {
      label: "Renewable/fossil fuels energy",
      value: "Renewable/fossil fuels energy",
    },
    {
      label: "Nuclear energy",
      value: "Nuclear energy",
    },
    {
      label: "Lighting systems",
      value: "Lighting systems",
    },
    {
      label: "Sensors, controls, test equipment",
      value: "Sensors, controls, test equipment",
    },
    {
      label: "Tools",
      value: "Tools",
    },
    {
      label: "Other",
      value: "Other",
    },
  ],
  Medical: [
    {
      label: "Diagnostic equipment",
      value: "Diagnostic equipment",
    },
    {
      label: "Instruments, devices",
      value: "Instruments, devices",
    },
    {
      label: "Imaging / scanning",
      value: "Imaging / scanning",
    },
    {
      label: "Other",
      value: "Other",
    },
  ],
  Military: [
    {
      label: "Aircraft, military",
      value: "Aircraft, military",
    },
    {
      label: "Aircraft, UAV",
      value: "Aircraft, UAV",
    },
    {
      label: "Communications equipment",
      value: "Communications equipment",
    },
    {
      label: "Computer systems",
      value: "Computer systems",
    },
    {
      label: "Missile systems",
      value: "Missile systems",
    },
    {
      label: "Satellites, military",
      value: "Satellites, military",
    },
    {
      label: "Sensors, controls, test equipment",
      value: "Sensors, controls, test equipment",
    },
    {
      label: "Vehicles, personnel transport",
      value: "Vehicles, personnel transport",
    },
    {
      label: "Vehicles, weapon transport",
      value: "Vehicles, weapon transport",
    },
    {
      label: "Weapons systems",
      value: "Weapons systems",
    },
    {
      label: "Other",
      value: "Other",
    },
  ],
};

export const SHIPPING_METHODS = [
  "DHL Express International",
  "FedEx International Priority",
  "UPS Worldwide Saver",
  "Priority Mail Express International",
];

export const mouserOrderTypes = [
  {
    label: "Rush",
    value: "Rush",
  },
  {
    label: "Complete",
    value: "Complete",
  },
];

export const BOM_TOOL_COLUMNS = [
  "Sno",
  "SKU",
  "Make",
  "Distributor",
  "HS Code",
  "Date Code",
  "RFQ QTY",
  "Stock",
  "MOQ",
  "MUL",
  "Price",
  "",
];
